import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  createUser,
  deleteUser,
  fetchUser,
  fetchUsers,
  fetchUsersByOrganisationId,
  updateUser,
  bulkImportUsers,
} from "../api/UserApi";

//--- get all users ---//
export const GetUsers = () => {
  return useQuery(["users"], fetchUsers);
};

//--- get users scoped to one organisation (superadmin cross-org read) ---//
// Hits the dedicated backend endpoint GET /users/organisation/:id, which
// returns the chosen organisation's users (not the caller's own). The key is
// prefixed with ["users", ...] so create/update/delete invalidations (which
// target ["users"]) still refetch it via react-query's prefix matching.
export const GetUsersByOrganisationId = (organisationId) => {
  return useQuery(
    ["users", "organisation", organisationId],
    fetchUsersByOrganisationId,
    {
      enabled: !!organisationId,
    }
  );
};

//--- Create user  ---//
export const CreateUser = (options) => {
  const queryClient = useQueryClient();

  return useMutation((newUser) => createUser(newUser), {
    onSuccess: (newUser) => {
      queryClient.invalidateQueries(["users"]);

      if (options?.onSuccess) options.onSuccess(newUser);
    },
    onError: options?.onError,
  });
};

//--- get user by id ---//
export const GetUserById = (userId) => {
  const queryClient = useQueryClient();
  return useQuery(["user", userId], fetchUser, {
    initialData: () => {
      const users = queryClient.getQueryData("users")?.data;
      if (users) {
        const user = users.find((user) => user.id === userId);
        if (user) {
          return { data: user };
        }
      }
      return undefined;
    },
  });
};

export const DeleteUser = (options) => {
  const queryClient = useQueryClient();

  return useMutation((userId) => deleteUser(userId), {
    onSuccess: () => {
      queryClient.invalidateQueries(["users"]);
      queryClient.invalidateQueries(["user"]);
    },
    ...options, // Spread custom handlers
  });
};

export const UpdateUser = (options) => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ userId, userData }) => updateUser(userId, userData),
    {
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries(["users"]);
        queryClient.invalidateQueries(["user", variables.userId]);
        if (options?.onSuccess) options.onSuccess(data, variables);
      },
      onError: options?.onError,
    }
  );
};

export const BulkImportUsers = (options) => {
  const queryClient = useQueryClient();

  return useMutation((formData) => bulkImportUsers(formData), {
    onSuccess: (response) => {
      queryClient.invalidateQueries(["users"]);
      if (options?.onSuccess) options.onSuccess(response);
    },
    onError: options?.onError,
  });
};
